import React, { useState, useMemo, useEffect, useRef } from 'react';
import Fuse from 'fuse.js';
import { ChevronDown } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import apiClient from '../../lib/apiClient';
import logger from '../../lib/logger';
import { VENDORS_QUERY_OPTIONS } from './cacheConfig';

const FUSE_OPTIONS = {
  includeScore: true,
  threshold: 0.4,
  ignoreLocation: true,
  minMatchCharLength: 1,
  keys: [
    { name: 'canonical', weight: 1.0 },
    // Sub-brands and acquired brands surface their parent canonical when
    // searched (e.g. typing "Splunk" or "Meraki" finds Cisco after
    // migration 1785600000000_consolidate_cisco_family.js).
    { name: 'aliases', weight: 0.5 },
  ],
};

function formatCurrency(num) {
  if (num >= 1e9) return `$${(num / 1e9).toFixed(1)}B`;
  if (num >= 1e6) return `$${(num / 1e6).toFixed(1)}M`;
  if (num >= 1e3) return `$${(num / 1e3).toFixed(0)}K`;
  return `$${Math.round(num || 0)}`;
}

function VendorPicker({ value, onChange }) {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const containerRef = useRef(null);

  const { data, isLoading, error, refetch, isFetching } = useQuery({
    queryKey: ['vendor-analysis', 'vendors'],
    queryFn: async () => {
      const t0 = performance.now();
      logger.vendorAnalysis.info('VendorPicker:fetch:start', { endpoint: '/api/vendor-analysis/vendors' });
      try {
        const result = await apiClient.get('/api/vendor-analysis/vendors');
        logger.vendorAnalysis.info('VendorPicker:fetch:ok', {
          duration_ms: Math.round(performance.now() - t0),
          count: result?.vendors?.length ?? 0,
        });
        return result;
      } catch (err) {
        logger.vendorAnalysis.error('VendorPicker:fetch:failed', {
          duration_ms: Math.round(performance.now() - t0),
          status: err?.status,
          code: err?.code,
        }, err);
        throw err;
      }
    },
    ...VENDORS_QUERY_OPTIONS,
  });

  const vendors = data?.vendors || [];
  const fuse = useMemo(() => new Fuse(vendors, FUSE_OPTIONS), [vendors]);

  const results = useMemo(() => {
    if (!query.trim()) return vendors.slice(0, 50);
    return fuse.search(query.trim()).map(r => r.item).slice(0, 50);
  }, [fuse, query, vendors]);

  useEffect(() => {
    function onClick(e) {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  return (
    <div className="vca-picker" ref={containerRef}>
      {/* Always a dropdown, GAS-style: the current vendor is the trigger and
          picking a new one replaces it in place. There is no "cleared" state
          to fall into — the page defaults to Palo Alto Networks and analysis
          is always on screen. */}
      <button
        type="button"
        className="vca-picker__trigger vca-picker__trigger--vendor"
        onClick={() => setOpen(o => !o)}
        aria-expanded={open}
        aria-haspopup="listbox"
      >
        <span className="vca-picker__trigger-label">
          {value || (isLoading ? 'Loading vendors…' : 'Select a vendor')}
        </span>
        <ChevronDown size={16} className="vca-picker__chevron" />
      </button>
      {error && (
        <div className="vca-section__error" style={{ marginTop: 6 }}>
          Vendor list failed to load: {error.message}
          {' '}
          <button
            type="button"
            className="vca-add-btn"
            onClick={() => refetch()}
            disabled={isFetching}
            style={{ marginLeft: 8 }}
          >
            Retry
          </button>
        </div>
      )}
      {open && (
        <div className="vca-picker__results" role="listbox">
          <input
            type="text"
            className="vca-picker__search"
            placeholder="Search vendors (Cisco, Elastic, Zscaler…)"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            autoFocus
          />
          {results.map(v => (
            <div
              key={v.canonical}
              className={`vca-picker__row ${v.canonical === value ? 'active' : ''}`}
              role="option"
              aria-selected={v.canonical === value}
              onClick={() => {
                logger.vendorAnalysis.info('VendorPicker:selected', {
                  vendor: v.canonical,
                  total_spend: v.total_spend,
                  agency_count: v.agency_count,
                });
                onChange(v.canonical);
                setOpen(false);
                setQuery('');
              }}
            >
              <div className="vca-picker__row-name">{v.canonical}</div>
              <div className="vca-picker__row-meta">
                <span>{formatCurrency(v.total_spend)}</span>
                <span>{v.agency_count} agencies</span>
              </div>
            </div>
          ))}
          {!results.length && !isLoading && (
            <div className="vca-picker__row vca-picker__row--empty">
              <div className="vca-picker__row-meta"><span>No vendor matches “{query}”</span></div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default VendorPicker;
